import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AuthDetailsUtilityPacimService, PacimAuthDetailsUtilityGroup , AuthorizationDetailsPacimService} from 'src/app/core';
import { EligibleDates, TherapyMinutes, TherapyMinutesIteration } from '@core/model';
import { AuthorizationReferenceProductDTO, UnsavedChangesConfirmation } from '@shared/model';
import { DateUtilities, TableColumn } from '@mynexus/mynexus-ui-lib';
import { dateWithoutMilliseconds } from '@shared/utility';
import { TherapyMinutesExpandedComponent } from './therapy-minutes-expanded/therapy-minutes-expanded.component';
import { Table } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'therapy-minutes-table',
    templateUrl: './therapy-minutes-table.component.html',
    styleUrls: ['./therapy-minutes-table.component.scss'],
    standalone: false
})
export class TherapyMinutesTableComponent implements OnChanges, OnInit, UnsavedChangesConfirmation {
  @Input() therapyMinutes: TherapyMinutes[];
  @Input() authorizationRange: Date[];
  @Input() tenantId: string;
  @Input() productId: string;
  @Input() authorizationByProductId: string;
  @Input() eligibleDatesForTherapy: EligibleDates[];
  @Input() canEdit: boolean;
  @Input() isInternal = false;

  @Output() updatedTherapy = new EventEmitter<TherapyMinutes[]>();

  @ViewChild(TherapyMinutesExpandedComponent) therapyMinutesExpandedComponent!: TherapyMinutesExpandedComponent;

  @ViewChild(Table) table: Table = null;
  iterationData: TherapyMinutesIteration[];
  disciplines: AuthorizationReferenceProductDTO[];
  tableCols: TableColumn[];

  isLoading = false;
  lineOfBusinessId: string | null;

  private readonly chunkSize = 7;

  get unsavedChanges(): boolean {
    return this.therapyMinutesExpandedComponent?.therapyForm?.dirty;
  }

  hasChanges(): boolean {
    return this.unsavedChanges;
  }

  discardChanges(): void {
    this.onCancel();
  }

  constructor(
    private authUtilities: AuthDetailsUtilityPacimService,
    private confirmationService: ConfirmationService,
    private readonly authorizationDetailService: AuthorizationDetailsPacimService,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.therapyMinutes?.currentValue && !changes.therapyMinutes?.firstChange) {
      this.getIterationData();
    }
  }

  ngOnInit(): void {
    this.getDisciplines();
  }

  onCancel(): void {
    this.therapyMinutesExpandedComponent.cancelChanges();
  }

  minutesUpdates(data: TherapyMinutes[]): void {
    this.therapyMinutes = data;
    this.getIterationData();
    this.updatedTherapy.emit(data);
  }

  onRowExpand(row: TherapyMinutesIteration, expanded: boolean): void {
    const expandedRowKey = Object.keys(this.table.expandedRowKeys)[0];

    if (expandedRowKey && this.unsavedChanges) {
      this.confirmationService.confirm({
        key: 'unsaved-changes-auth-detail',
        message:
          'You have unsaved changes that will be lost. Are you sure you want to continue?',
        accept: () => {
          this.table.expandedRowKeys = {[row.weekNumber]: !expanded};
        },
      });
    } else {
      this.table.expandedRowKeys = {[row.weekNumber]: !expanded};
    }
  }

  private getIterationData(): void {
    const authDates = DateUtilities.getDatesRange(this.authorizationRange[0], this.authorizationRange[1])
      .map(item => dateWithoutMilliseconds(DateUtilities.convertToUTCStartOfDayDate(item)?.toISOString()));

    this.iterationData = this.chunckArray(authDates).map((iteration, index) => {
      const addedMinutes = this.getMinutes(iteration);

      return {
        weekNumber: ++index,
        dates: iteration,
        startDate: iteration[0],
        endDate: iteration[iteration.length - 1],
        addedMinutes,
        totalMinutes: this.getTotalValue(addedMinutes),
      }
    });
  }

  private chunckArray(array: string[]): any[] {
    let index = 0;
    let resIndex = 0;
    const result = [];

    while (index < array.length) {
      result[resIndex++] = array.slice(index, (index += this.chunkSize));
    }

    return result;
  }

  private getDisciplines(): void {
    this.isLoading = true;
    this.authorizationDetailService.getAuthorizationDetails(this.tenantId, this.authorizationByProductId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data =>{
        this.lineOfBusinessId = data.authorizationProduct.lineOfBusinessId;
      });

    this.authUtilities.getUtility(this.tenantId, this.productId, PacimAuthDetailsUtilityGroup.TherapyDiscipline, this.lineOfBusinessId)
      .subscribe(data => {
        this.disciplines = data.sort((firstItem, secondItem) => firstItem.sortOrder - secondItem.sortOrder);

        this.initTable();
        this.getIterationData();

        this.isLoading = false;
      });
  }

  private initTable(): void {
    this.tableCols = [
      new TableColumn('', 'expand', '3rem'),
      new TableColumn('Week', 'weekNumber', '19rem'),
      new TableColumn('Period', 'period', '19rem'),
    ];

    this.disciplines.forEach(discipline => {
      this.tableCols.push(new TableColumn(`Total ${discipline.description}`, discipline.description, '19rem'));
    });
  }

  private getTotalValue(minutes: TherapyMinutes[]): Record<string, number> {
    const total = {};

    this.disciplines.forEach(discipline => {
      total[discipline.description] = minutes
        .filter(item => item.therapyDiscipline.authorizationReferenceByProductId === discipline.authorizationReferenceByProductId)
        .reduce((accumulator, currentValue) => accumulator + currentValue.timeSpent, 0);
    })

    return total;
  }

  private getMinutes(period: string[]): TherapyMinutes[] {
    return this.therapyMinutes.filter(item => period.includes(item.therapyDate));
  }
}
